// EmployeeVehicleTable.tsx
import React, { useState } from 'react';
import { Table, Button, Pagination, Spinner, Form, Row, Col } from 'react-bootstrap';
import { EmployeeVehicle } from './types';

interface Props {
  assignments: EmployeeVehicle[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onView: (item: EmployeeVehicle) => void;
  onEdit: (item: EmployeeVehicle) => void;
  onDelete: (id: number) => void;
  onStatusFilterChange: (status: string) => void; // ← New prop to notify parent
}

const EmployeeVehicleTable: React.FC<Props> = ({
  assignments,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onStatusFilterChange,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL'); // Default: show all

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedStatus(value);
    onStatusFilterChange(value); // Notify parent to refetch data
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading assignments...</p>
      </div>
    );
  }

  return (
    <>
      {/* Filter Controls */}
      <Row className="mb-3 align-items-center">
        <Col md={4}>
          <Form.Label className="fw-bold mb-1">Filter by Status</Form.Label>
          <Form.Select
            value={selectedStatus}
            onChange={handleStatusChange}
            className="w-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </Form.Select>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Emp No</th>
              <th>Number Plate</th>
              <th>Type</th>
              <th>Brand</th>
              <th>Color</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  {selectedStatus !== 'ALL'
                    ? `No ${selectedStatus.toLowerCase()} assignments found`
                    : 'No vehicle assignments found'}
                </td>
              </tr>
            ) : (
              assignments.map(item => (
                <tr key={item.id}>
                  <td>{item.employee.employeeName}</td>
                  <td>{item.employee.employeeNumber}</td>
                  <td className="fw-bold">{item.vehicleNumberPlate}</td>
                  <td>{item.vehicleTypeName}</td>
                  <td>{item.vehicleBrandName}</td>
                  <td>{item.vehicleColor}</td>
                  <td>
                    <span
                      className={`badge bg-${
                        item.status === 'ACTIVE' ? 'success' : 'danger'
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => onView(item)}
                      title="View"
                    >
                      <i className="fas fa-eye"></i>
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      className="me-1"
                      onClick={() => onEdit(item)}
                      title="Edit"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(item.id)}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination size="sm">
            <Pagination.Prev
              onClick={() => onPageChange(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i}
                active={i === currentPage}
                onClick={() => onPageChange(i)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
            />
          </Pagination>
        </div>
      )}
    </>
  );
};

export default EmployeeVehicleTable;